import { useState, useEffect, useRef, useCallback, memo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../../stores/authStore'
import reelService, { Reel, ReelComment } from '../../services/reelService'
import { GovLayout, GovPageTitle, GovCard, GovButton, GovEmptyState } from '@/components/layout/GovLayout'
import {
  Heart, MessageCircle, Bookmark, Volume2, VolumeX,
  Play, ChevronUp, ChevronDown, X, Send, Plus, Trash2, Repeat, Flag
} from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { HashtagText } from '@/lib/hashtagLinkify'
import ReportModal from '@/components/common/ReportModal'
import ShareMenu from '@/components/common/ShareMenu'

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================
const formatCount = (count: number): string => {
  if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`
  if (count >= 1000) return `${(count / 1000).toFixed(1)}K`
  return String(count)
}

const getMediaUrl = (url: string): string => {
  if (!url) return ''
  if (url.startsWith('http')) return url
  // Don't add /api prefix to /uploads/ - nginx serves these directly
  if (url.startsWith('/uploads/')) return url
  return url.startsWith('/') ? `/api${url}` : `/api/${url}`
}

// ============================================================================
// MEMOIZED COMMENT ITEM
// ============================================================================
const CommentItem = memo(({ comment }: { comment: ReelComment }) => (
  <div className="flex gap-3">
    <img
      src={comment.user.profileImage ? getMediaUrl(comment.user.profileImage) : '/default-avatar.svg'}
      alt={comment.user.firstName}
      className="w-8 h-8 rounded-full object-cover flex-shrink-0"
      loading="lazy"
    />
    <div className="flex-1 min-w-0">
      <p className="text-sm">
        <span className="font-semibold text-gray-900">{comment.user.firstName} {comment.user.lastName}</span>
        <span className="text-gray-600 ml-2">{comment.content}</span>
      </p>
      <p className="text-xs text-gray-500 mt-1">
        {formatDistanceToNow(new Date(comment.createdAt))} ago
      </p>
    </div>
  </div>
))

CommentItem.displayName = 'CommentItem'

// ============================================================================
// ACTION BUTTON COMPONENT
// ============================================================================
const ActionButton = memo(({
  icon: Icon,
  count,
  active,
  onClick,
  disabled,
  activeColor = 'text-red-500',
  label,
}: {
  icon: React.ComponentType<{ className?: string }>
  count?: number
  active?: boolean
  onClick: () => void
  disabled?: boolean
  activeColor?: string
  label: string
}) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className="flex flex-col items-center gap-1 group"
    aria-label={label}
  >
    <div className={`w-11 h-11 rounded-full flex items-center justify-center transition-all
      ${active ? `bg-gray-100 scale-110` : 'bg-gray-100 group-hover:bg-gray-200'}
      ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
      <Icon className={`w-6 h-6 ${active ? activeColor : 'text-gray-700'}`} />
    </div>
    {count !== undefined && (
      <span className="text-xs font-medium text-gray-600">{formatCount(count)}</span>
    )}
  </button>
))

ActionButton.displayName = 'ActionButton'

// ============================================================================
// MAIN REELS PAGE COMPONENT
// ============================================================================
const ReelsPage = () => {
  const { user, isAuthenticated } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()
  const [reels, setReels] = useState<Reel[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [loading, setLoading] = useState(true)
  // Two sections: 'recent' = public reels by ALL users (newest first, up to 500),
  // 'my' = the current user's own posted reels. Default shows public reels.
  const [activeSection, setActiveSection] = useState<'recent' | 'my'>('recent')
  const [isMuted, setIsMuted] = useState(true)
  const [showComments, setShowComments] = useState(false)
  const [comments, setComments] = useState<ReelComment[]>([])
  const [commentsLoading, setCommentsLoading] = useState(false)
  const [commentText, setCommentText] = useState('')
  const [likedReels, setLikedReels] = useState<Set<number>>(new Set())
  const [bookmarkedReels, setBookmarkedReels] = useState<Set<number>>(new Set())
  const [resharedReels, setResharedReels] = useState<Set<number>>(new Set())
  const [isLiking, setIsLiking] = useState(false)
  const [isBookmarking, setIsBookmarking] = useState(false)
  const [isResharing, setIsResharing] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const touchStartY = useRef(0)

  // Stop video on unmount
  useEffect(() => {
    return () => {
      if (videoRef.current) {
        videoRef.current.pause()
        videoRef.current.src = ''
        videoRef.current.load()
      }
    }
  }, [location])

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login')
      return
    }
  }, [isAuthenticated, navigate])

  // Auto-play current video
  useEffect(() => {
    const video = videoRef.current
    if (video && reels[currentIndex]) {
      video.src = getMediaUrl(reels[currentIndex].videoUrl)
      video.muted = isMuted
      video.play().catch(() => {})
    }
    return () => { if (video) video.pause() }
  }, [currentIndex, reels, isMuted])

  const fetchReels = useCallback(async () => {
    try {
      setLoading(true)
      // 'my' = current user's own posted reels; 'recent' = public reels by all users (newest first, up to 500)
      const response = activeSection === 'my'
        ? await reelService.getFeed(0, 50)
        : await reelService.getPublic(0, 500)
      setReels(response.content || [])
      setCurrentIndex(0)
      const liked = new Set<number>()
      const reshared = new Set<number>()
      response.content?.forEach((reel) => {
        if (reel.likedByMe) liked.add(reel.id)
        if (reel.isReshared) reshared.add(reel.id)
      })
      setLikedReels(liked)
      setResharedReels(reshared)

      // Load bookmarked status
      try {
        const savedResponse = await reelService.getSavedReels(0, 100)
        const bookmarked = new Set<number>()
        savedResponse.content?.forEach((reel) => bookmarked.add(reel.id))
        setBookmarkedReels(bookmarked)
      } catch { /* ignore */ }
    } catch (error) {
      console.error('Error fetching reels:', error)
    } finally {
      setLoading(false)
    }
  }, [activeSection])

  // Fetch the active section's reels. Re-runs whenever the section changes
  // (fetchReels identity depends on activeSection) and on mount.
  useEffect(() => {
    if (isAuthenticated) fetchReels()
  }, [fetchReels, isAuthenticated])

  const fetchComments = useCallback(async (reelId: number) => {
    try {
      setCommentsLoading(true)
      const response = await reelService.getComments(reelId)
      setComments(response.content || [])
    } catch {
      setComments([])
    } finally {
      setCommentsLoading(false)
    }
  }, [])

  const handleLike = useCallback(async () => {
    const reel = reels[currentIndex]
    if (!reel || isLiking) return

    setIsLiking(true)
    try {
      if (likedReels.has(reel.id)) {
        await reelService.unlike(reel.id)
        setLikedReels(prev => { const next = new Set(prev); next.delete(reel.id); return next })
        setReels(prev => prev.map(r => r.id === reel.id ? { ...r, likeCount: Math.max(0, r.likeCount - 1) } : r))
      } else {
        await reelService.like(reel.id)
        setLikedReels(prev => new Set(prev).add(reel.id))
        setReels(prev => prev.map(r => r.id === reel.id ? { ...r, likeCount: r.likeCount + 1 } : r))
      }
    } catch {
      alert('Failed to like. Please try again.')
    } finally {
      setIsLiking(false)
    }
  }, [currentIndex, reels, likedReels, isLiking])

  const handleBookmark = useCallback(async () => {
    const reel = reels[currentIndex]
    if (!reel || isBookmarking) return

    setIsBookmarking(true)
    try {
      const result = await reelService.toggleBookmark(reel.id)
      if (result) {
        setBookmarkedReels(prev => new Set(prev).add(reel.id))
      } else {
        setBookmarkedReels(prev => { const next = new Set(prev); next.delete(reel.id); return next })
      }
    } catch (error: any) {
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        alert('Please log in to bookmark reels')
        navigate('/login')
      } else {
        alert('Failed to bookmark. Please try again.')
      }
    } finally {
      setIsBookmarking(false)
    }
  }, [currentIndex, reels, isBookmarking, navigate])

  // Share: the dropdown UI lives in <ShareMenu> (copy link + app options +
  // native sheet). The server share-count bump is best-effort — on the public
  // /reels page an anonymous viewer's POST /reels/{id}/share 403s and is
  // swallowed here so the copy/share still succeeds with no error alert.
  const handleSharedCount = useCallback((reelId: number) => {
    reelService.share(reelId)
      .then(() => setReels(prev => prev.map(r => r.id === reelId ? { ...r, shareCount: r.shareCount + 1 } : r)))
      .catch(() => {})
  }, [])

  const handleView = useCallback(async () => {
    const reel = reels[currentIndex]
    if (!reel) return
    try { await reelService.view(reel.id) } catch { /* ignore */ }
  }, [currentIndex, reels])

  // Reshare toggle — idempotent on the server. Optimistic UI in the rail;
  // reconcile reshareCount from the server response.
  const handleReshare = useCallback(async () => {
    const reel = reels[currentIndex]
    if (!reel || isResharing) return
    setIsResharing(true)
    try {
      if (resharedReels.has(reel.id)) {
        await reelService.unreshare(reel.id)
        setResharedReels(prev => { const next = new Set(prev); next.delete(reel.id); return next })
        setReels(prev => prev.map(r => r.id === reel.id ? { ...r, reshareCount: Math.max(0, r.reshareCount - 1) } : r))
      } else {
        await reelService.reshare(reel.id)
        setResharedReels(prev => new Set(prev).add(reel.id))
        setReels(prev => prev.map(r => r.id === reel.id ? { ...r, reshareCount: r.reshareCount + 1 } : r))
      }
    } catch {
      alert('Failed to reshare. Please try again.')
    } finally {
      setIsResharing(false)
    }
  }, [currentIndex, reels, resharedReels, isResharing])

  const [isDeletingReel, setIsDeletingReel] = useState(false)
  const [reportReelId, setReportReelId] = useState<number | null>(null)
  const handleDeleteReel = useCallback(async () => {
    const reel = reels[currentIndex]
    if (!reel || isDeletingReel) return
    if (!window.confirm('Delete this reel permanently? This cannot be undone.')) return
    setIsDeletingReel(true)
    try {
      await reelService.delete(reel.id)
      const remaining = reels.filter(r => r.id !== reel.id)
      setReels(remaining)
      if (remaining.length === 0) {
        navigate('/feed')
      } else if (currentIndex >= remaining.length) {
        setCurrentIndex(remaining.length - 1)
      }
    } catch {
      alert('Could not delete the reel. Please try again.')
    } finally {
      setIsDeletingReel(false)
    }
  }, [currentIndex, reels, isDeletingReel, navigate])

  useEffect(() => { handleView() }, [handleView])

  const handleOpenComments = useCallback(() => {
    const reel = reels[currentIndex]
    if (reel) fetchComments(reel.id)
    setShowComments(true)
  }, [currentIndex, reels, fetchComments])

  const handleAddComment = useCallback(async () => {
    const reel = reels[currentIndex]
    if (!reel || !commentText.trim()) return

    try {
      const newComment = await reelService.addComment(reel.id, commentText.trim())
      setComments(prev => [newComment, ...prev])
      setCommentText('')
      setReels(prev => prev.map(r => r.id === reel.id ? { ...r, commentCount: r.commentCount + 1 } : r))
    } catch {
      alert('Failed to add comment. Please try again.')
    }
  }, [currentIndex, reels, commentText])

  const toggleMute = useCallback(() => {
    setIsMuted(prev => {
      const newMuted = !prev
      if (videoRef.current) videoRef.current.muted = newMuted
      return newMuted
    })
  }, [])

  const togglePlay = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.paused ? videoRef.current.play() : videoRef.current.pause()
    }
  }, [])

  // Touch navigation
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY
  }, [])

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    const diff = touchStartY.current - e.changedTouches[0].clientY
    if (Math.abs(diff) > 50) {
      if (diff > 0 && currentIndex < reels.length - 1) setCurrentIndex(prev => prev + 1)
      else if (diff < 0 && currentIndex > 0) setCurrentIndex(prev => prev - 1)
    }
  }, [currentIndex, reels.length])

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (showComments) return
      if (e.key === 'ArrowUp' && currentIndex < reels.length - 1) setCurrentIndex(prev => prev + 1)
      else if (e.key === 'ArrowDown' && currentIndex > 0) setCurrentIndex(prev => prev - 1)
      else if (e.key === ' ') { e.preventDefault(); togglePlay() }
      else if (e.key === 'm') toggleMute()
      else if (e.key === 'l') handleLike()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [currentIndex, reels.length, showComments, toggleMute, togglePlay, handleLike])

  // Wheel navigation
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault()
      if (e.deltaY > 0 && currentIndex < reels.length - 1) setCurrentIndex(prev => prev + 1)
      else if (e.deltaY < 0 && currentIndex > 0) setCurrentIndex(prev => prev - 1)
    }
    const container = containerRef.current
    if (container) container.addEventListener('wheel', handleWheel, { passive: false })
    return () => { if (container) container.removeEventListener('wheel', handleWheel) }
  }, [currentIndex, reels.length])

  // Loading state
  if (loading) {
    return (
      <GovLayout showSidebar={false}>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </GovLayout>
    )
  }

  // Empty state
  if (reels.length === 0) {
    const isMySection = activeSection === 'my'
    return (
      <GovLayout showSidebar={false}>
        <GovPageTitle title="TB Awareness Reels" subtitle="Watch and create short videos about TB" />
        <div className="max-w-md mx-auto">
          <div className="flex gap-2 mb-4 justify-center">
            <button
              onClick={() => setActiveSection('recent')}
              className={`px-4 py-1.5 text-sm font-medium rounded-full transition-colors ${
                activeSection === 'recent' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Recent
            </button>
            <button
              onClick={() => setActiveSection('my')}
              className={`px-4 py-1.5 text-sm font-medium rounded-full transition-colors ${
                activeSection === 'my' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              My Reels
            </button>
          </div>
          <GovEmptyState
            icon="🎬"
            title={isMySection ? "You haven't posted any reels yet" : 'No Reels Yet'}
            description={isMySection
              ? 'Create your first reel about TB awareness!'
              : 'Be the first to create a reel about TB awareness!'}
            action={{
              label: 'Create Reel',
              onClick: () => navigate('/create-reel')
            }}
          />
          <div className="mt-4 flex justify-center">
            <GovButton variant="secondary" onClick={() => navigate('/feed')}>
              Go to Feed
            </GovButton>
          </div>
        </div>
      </GovLayout>
    )
  }

  const currentReel = reels[currentIndex]

  return (
    <GovLayout showSidebar={false}>
      <GovPageTitle title="TB Awareness Reels" subtitle="Learn about TB through short videos" />

      <div className="max-w-md mx-auto">
        {/* Section toggle: Recent (public reels by all users) | My Reels (your own) */}
        <div className="flex gap-2 mb-4 justify-center">
          <button
            onClick={() => setActiveSection('recent')}
            className={`px-4 py-1.5 text-sm font-medium rounded-full transition-colors ${
              activeSection === 'recent' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Recent
          </button>
          <button
            onClick={() => setActiveSection('my')}
            className={`px-4 py-1.5 text-sm font-medium rounded-full transition-colors ${
              activeSection === 'my' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            My Reels
          </button>
        </div>

        {/* Video Player Card */}
        <GovCard className="overflow-hidden p-0">
          <div
            ref={containerRef}
            className="relative aspect-[9/16] bg-black rounded-xl overflow-hidden"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            {/* Video */}
            <video
              ref={videoRef}
              poster={currentReel.thumbnailUrl ? getMediaUrl(currentReel.thumbnailUrl) : undefined}
              className="absolute inset-0 w-full h-full object-cover"
              loop
              playsInline
              muted={isMuted}
              onClick={togglePlay}
            />

            {/* Gradient Overlay */}
            <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-black/40 via-transparent to-black/60" />

            {/* Top Bar */}
            <div className="absolute top-0 left-0 right-0 p-3 flex items-center justify-between z-20">
              <button
                onClick={() => navigate('/feed')}
                className="p-2 text-white bg-black/30 backdrop-blur-sm rounded-full hover:bg-black/50"
                aria-label="Back to feed"
              >
                <ChevronDown className="w-5 h-5 rotate-45" />
              </button>
              <h2 className="text-white font-semibold text-base drop-shadow-lg">Reels</h2>
              <button
                onClick={toggleMute}
                className="p-2 text-white bg-black/50 backdrop-blur-sm rounded-full hover:bg-black/70"
                aria-label={isMuted ? 'Unmute' : 'Mute'}
              >
                {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </button>
            </div>

            {/* Mute indicator */}
            {isMuted && (
              <div
                onClick={toggleMute}
                className="absolute top-14 right-3 z-30 cursor-pointer"
              >
                <div className="bg-red-500/80 text-white px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 hover:bg-red-600/80">
                  <VolumeX className="w-4 h-4" />
                  <span>Tap to Unmute</span>
                </div>
              </div>
            )}

            {/* Right Side Actions */}
            <div className="absolute right-3 bottom-20 flex flex-col items-center gap-4 z-30">
              <ActionButton
                icon={Heart}
                count={currentReel.likeCount + (likedReels.has(currentReel.id) ? 1 : 0)}
                active={likedReels.has(currentReel.id)}
                onClick={handleLike}
                disabled={isLiking}
                activeColor="text-red-500"
                label="Like"
              />

              <ActionButton
                icon={MessageCircle}
                count={currentReel.commentCount}
                onClick={handleOpenComments}
                label="Comments"
              />

              <ActionButton
                icon={Repeat}
                count={currentReel.reshareCount}
                active={resharedReels.has(currentReel.id)}
                onClick={handleReshare}
                disabled={isResharing}
                activeColor="text-green-500"
                label="Reshare"
              />

              <div className="flex flex-col items-center gap-1">
                <ShareMenu
                  url={`${window.location.origin}/reel/${currentReel.id}`}
                  title="TB Awareness Reel"
                  text="Check out this reel on TB Mukt Bharat"
                  onShared={() => handleSharedCount(currentReel.id)}
                  triggerClassName="w-11 h-11 rounded-full flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-700 transition-all"
                />
                <span className="text-xs font-medium text-gray-600">{formatCount(currentReel.shareCount)}</span>
              </div>

              <ActionButton
                icon={Bookmark}
                active={bookmarkedReels.has(currentReel.id)}
                onClick={handleBookmark}
                disabled={isBookmarking}
                activeColor="text-yellow-500"
                label="Save"
              />

              {user && currentReel.user.id === user.id && (
                <ActionButton
                  icon={Trash2}
                  onClick={handleDeleteReel}
                  disabled={isDeletingReel}
                  activeColor="text-red-500"
                  label="Delete"
                />
              )}

              {user && currentReel.user.id !== user.id && (
                <ActionButton
                  icon={Flag}
                  onClick={() => setReportReelId(currentReel.id)}
                  activeColor="text-red-500"
                  label="Report"
                />
              )}
            </div>

            {/* Bottom Info */}
            <div className="absolute bottom-0 left-0 right-0 p-3 z-20">
              <div className="flex items-center gap-2.5 mb-2">
                <img
                  src={currentReel.user.profileImage ? getMediaUrl(currentReel.user.profileImage) : '/default-avatar.svg'}
                  alt={currentReel.user.firstName}
                  className="w-9 h-9 rounded-full object-cover border-2 border-white/30"
                  loading="lazy"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium text-sm drop-shadow-lg truncate">
                    {currentReel.user.firstName} {currentReel.user.lastName}
                  </p>
                  <p className="text-gray-300 text-xs">@{currentReel.user.username}</p>
                </div>
              </div>

              {currentReel.caption && (
                <p className="text-white text-sm mb-2 line-clamp-2 drop-shadow-lg">
                  <HashtagText
                    content={currentReel.caption}
                    onTagClick={(tag) => navigate(`/hashtag/${encodeURIComponent(tag.toLowerCase())}`)}
                    tagClassName="text-blue-300 hover:underline font-medium"
                  />
                </p>
              )}

              {currentReel.hashtags && currentReel.hashtags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {currentReel.hashtags.map((tag) => (
                    <button
                      key={tag}
                      onClick={() => navigate(`/hashtag/${encodeURIComponent(tag.toLowerCase())}`)}
                      className="px-2 py-0.5 bg-white/15 text-white text-xs font-medium rounded-full hover:bg-white/25"
                    >
                      #{tag}
                    </button>
                  ))}
                </div>
              )}

              {currentReel.musicTitle && (
                <p className="text-white/80 text-xs drop-shadow-lg truncate">
                  ♪ {currentReel.musicTitle}{currentReel.musicArtist && ` - ${currentReel.musicArtist}`}
                </p>
              )}

              <p className="text-gray-400 text-xs mt-1.5">
                {formatDistanceToNow(new Date(currentReel.createdAt))} ago · {formatCount(currentReel.viewCount)} views
              </p>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 flex-col gap-2 z-30">
              {currentIndex > 0 && (
                <button
                  onClick={() => setCurrentIndex(prev => prev - 1)}
                  className="w-9 h-9 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/60"
                  aria-label="Previous reel"
                >
                  <ChevronUp className="w-5 h-5" />
                </button>
              )}
              {currentIndex < reels.length - 1 && (
                <button
                  onClick={() => setCurrentIndex(prev => prev + 1)}
                  className="w-9 h-9 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/60"
                  aria-label="Next reel"
                >
                  <ChevronDown className="w-5 h-5" />
                </button>
              )}
            </div>

            {/* Progress Indicator */}
            <div className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 flex-col gap-1.5 z-30">
              {reels.slice(0, Math.min(reels.length, 10)).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-1.5 rounded-full transition-all ${index === currentIndex ? 'h-5 bg-white' : 'h-1.5 bg-white/40 hover:bg-white/60'}`}
                  aria-label={`Go to reel ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </GovCard>

        {/* Mobile Navigation Hint */}
        <div className="sm:hidden text-center text-gray-500 text-xs mt-2 flex items-center justify-center gap-2">
          <ChevronUp className="w-4 h-4" />
          <span>Swipe to navigate</span>
          <ChevronDown className="w-4 h-4" />
        </div>

        {/* Create Reel Button */}
        <div className="mt-4 flex justify-center">
          <GovButton onClick={() => navigate('/create-reel')} className="flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Create Reel
          </GovButton>
        </div>
      </div>

      {/* Comments Drawer */}
      {showComments && (
        <div className="fixed inset-0 bg-black/70 z-50" onClick={() => setShowComments(false)}>
          <div
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl max-h-[70vh] flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-bold">Comments ({currentReel.commentCount})</h3>
              <button onClick={() => setShowComments(false)} aria-label="Close comments">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {commentsLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : comments.length === 0 ? (
                <p className="text-center text-gray-500 py-8">
                  No comments yet. Be the first to comment!
                </p>
              ) : (
                <div className="space-y-4">
                  {comments.map(comment => (
                    <CommentItem key={comment.id} comment={comment} />
                  ))}
                </div>
              )}
            </div>

            <div className="p-4 border-t">
              <div className="flex gap-2">
                <img
                  src={user?.profileImage ? getMediaUrl(user.profileImage) : '/default-avatar.svg'}
                  alt={user?.firstName}
                  className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                  loading="lazy"
                />
                <input
                  type="text"
                  value={commentText}
                  onChange={e => setCommentText(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAddComment()}
                  placeholder="Add a comment..."
                  className="flex-1 px-4 py-2 rounded-full bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <GovButton onClick={handleAddComment} disabled={!commentText.trim()}>
                  <Send className="w-4 h-4" />
                </GovButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Report reel modal — shown when a viewer flags a reel they don't own */}
      {reportReelId && (
        <ReportModal reelId={reportReelId} onClose={() => setReportReelId(null)} />
      )}
    </GovLayout>
  )
}

export default memo(ReelsPage)